/**
 * @file Builds the description rows of an SDIRA investment profile.
 */
import React from "react";
import { Space, Typography } from "antd";
import type { ReactNode } from "react";

import { Value } from "@components/Text";
import {
  CUSTODIAN_REVIEWED_SDIRA,
  CHECKBOOK_CONTROLLED_SDIRA,
} from "@api/services/investments/shapes/SdiraTypeEnum";

import renderAdditional from "./renderAdditional";
import type { DescriptionItem, Investor, ProfileSdira } from "./types";

const LABEL_TYPE = "SDIRA Type";
const LABEL_NAME = "Full Legal SDIRA Account Name";
const LABEL_OTHER = "Other";
const LABEL_CUSTODIAN = "Custodian";
const LABEL_ENTITY_EIN = "Entity EIN";
const LABEL_INVESTMENTS = "Investments";
const LABEL_ENTITY_NAME = "Entity’s Full Legal Name";

const FULL_ROW_SPAN = 2;

const TYPE_LABELS_MAP: Record<string, string> = {
  [CUSTODIAN_REVIEWED_SDIRA]: "Custodian-reviewed SDIRA",
  [CHECKBOOK_CONTROLLED_SDIRA]: "Checkbook-controlled SDIRA",
};

/**
 * Builds the description rows of an SDIRA investment profile.
 */
const renderProfileSdira = (
  profileSdira: ProfileSdira,
  formattedInvestments?: ReactNode,
  // NOTE: The investor is not used, and the parameter keeps the same
  //       position as in the other profile renderers.
  _investor?: Investor,
  additionalExtra?: ReactNode,
): DescriptionItem[] => {
  const {
    sdiraType,
    entityName,
    hasEinNumber,
    einNumber,
    accountName,
    custodianName,
    custodianAddress = {},
    custodianEinNumber,
  } = profileSdira;

  const items: DescriptionItem[] = [];

  const isCheckbook = sdiraType === CHECKBOOK_CONTROLLED_SDIRA;
  const isOtherCustodian = !custodianAddress.addressRegion;

  items.push({
    span: FULL_ROW_SPAN,
    label: LABEL_TYPE,
    value: TYPE_LABELS_MAP[sdiraType],
  });

  if (isCheckbook) {
    items.push({
      span: FULL_ROW_SPAN,
      label: LABEL_ENTITY_NAME,
      value: entityName,
    });

    if (hasEinNumber) {
      items.push({
        span: FULL_ROW_SPAN,
        label: LABEL_ENTITY_EIN,
        value: <Value value={einNumber} isEin />,
      });
    }
  }

  items.push({
    span: FULL_ROW_SPAN,
    label: LABEL_NAME,
    value: accountName,
  });

  if (formattedInvestments) {
    items.push({
      span: FULL_ROW_SPAN,
      label: LABEL_INVESTMENTS,
      value: formattedInvestments,
    });
  }

  const custodianValue = isOtherCustodian
    ? LABEL_OTHER
    : (
      <Space direction="vertical" size={0}>
        <Typography.Text>
          <Typography.Text strong>
            {custodianName}
          </Typography.Text>
          {" "}
          <Typography.Text type="secondary">
            (<Value value={custodianEinNumber} isEin />)
          </Typography.Text>
        </Typography.Text>

        <Value value={custodianAddress} />
      </Space>
    );

  items.push({
    span: FULL_ROW_SPAN,
    label: LABEL_CUSTODIAN,
    value: custodianValue,
  });

  items.push(renderAdditional(profileSdira, additionalExtra));

  return items;
};

export default renderProfileSdira;
